import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { AgentToolConfirmation } from 'AgentApp/ts/interfaces/agent-event';

import { AlertConfirmationDetailsComponent } from './alert-confirmation-details/alert-confirmation-details.component';
import { EditPortfolioConfirmationDetailsComponent } from './edit-portfolio-confirmation-details/edit-portfolio-confirmation-details.component';
import { UpdateWatchlistConfirmationDetailsComponent } from './update-watchlist-confirmation-details/update-watchlist-confirmation-details.component';

export interface AgentToolPermissionChoiceData {
    authorize: boolean;
    alwaysApply: boolean;
    reason: string | null; // tool call rejection reason, sent to the model
}

@Component({
    selector: 'ycn-agent-tool-permission-box',
    imports: [
        FormsModule,
        AlertConfirmationDetailsComponent,
        EditPortfolioConfirmationDetailsComponent,
        UpdateWatchlistConfirmationDetailsComponent,
    ],
    templateUrl: './agent-tool-permission-box.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentToolPermissionBoxComponent {
    protected readonly toolCall = input.required<AgentToolConfirmation>();

    readonly confirm = output<AgentToolPermissionChoiceData>();

    protected readonly alwaysApply = model(false);

    protected readonly displayName = computed(() => this.toolCall().display_name || this.toolCall().name);

    protected readonly details = computed(() => this.toolCall().details ?? null);

    protected readonly updateWatchlistDetails = computed(() => {
        const details = this.details();
        return details?.kind === 'update_watchlist' ? details : null;
    });

    protected readonly editPortfolioDetails = computed(() => {
        const details = this.details();
        return details?.kind === 'edit_portfolio' ? details : null;
    });

    protected readonly alertDetails = computed(() => {
        const details = this.details();
        return details?.kind === 'alert' ? details : null;
    });

    protected readonly hasError = computed(() => !!this.details()?.error);

    authorize(): void {
        this.confirm.emit({ authorize: true, alwaysApply: this.alwaysApply(), reason: null });
    }

    reject(): void {
        this.confirm.emit({ authorize: false, alwaysApply: this.alwaysApply(), reason: null });
    }

    dismiss(): void {
        const error = this.details()?.error ?? null;
        const reason = error ? `The action could not be prepared: ${error}` : null;
        this.confirm.emit({ authorize: false, alwaysApply: false, reason });
    }
}
